import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import { useAuth } from '../../lib/auth'
import { portalFor } from '../../lib/roles'
import type { HrStaffRow } from '../../types'
import { Alert, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge } from '../../components/ui'
import { UsersGroupIcon } from '../../components/icons'

interface StaffResponse { ok: boolean; items: HrStaffRow[] }

export default function Staff() {
  const { user } = useAuth()
  const isAdmin = portalFor(user) === 'admin'
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'hr', 'staff'],
    queryFn: () => api.get<StaffResponse>('/ops/hr/staff'),
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Staff records</h1>
          <p className="page-head__sub">
            {isAdmin
              ? 'Employment, contract and medical status across every organisation.'
              : "Employment, contract and medical status for your organisation's staff."}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader title="Staff" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<UsersGroupIcon size={22} />} title="No staff found" body="Staff members will appear here." />
          ) : (
            <>
              {/* PWA tap rows - the 5-column table clipped everything past
                  Role off a phone screen; desktop keeps the full table. */}
              <div className="mobile-only">
                {items.map(({ user, record }) => (
                  <Link key={user.uid} to={`/hr/${user.uid}`} className="lrow">
                    <div className="lrow__main">
                      <div className="lrow__title">{user.full_name || user.email || user.uid}</div>
                      <div className="lrow__sub">
                        {[titleCase(user.role), record.contract_type ? titleCase(record.contract_type) : null]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="lrow__end">
                      {record.employment_status ? <StatusBadge status={record.employment_status} /> : null}
                    </div>
                  </Link>
                ))}
              </div>
            <div className="table-wrap desktop-only">
              <table className="table">
                <thead><tr><th>Name</th><th>Role</th><th>Employment</th><th>Contract</th><th>Medical</th></tr></thead>
                <tbody>
                  {items.map(({ user, record }) => (
                    <tr key={user.uid}>
                      <td>
                        <Link to={`/hr/${user.uid}`} style={{ fontWeight: 600 }}>
                          {user.full_name || user.email || user.uid}
                        </Link>
                      </td>
                      <td>{titleCase(user.role)}</td>
                      <td>{record.employment_status ? <StatusBadge status={record.employment_status} /> : '—'}</td>
                      <td>{record.contract_type ? titleCase(record.contract_type) : '—'}</td>
                      <td>{record.medical_status ? <StatusBadge status={record.medical_status} /> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
